import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';
import { pageContext } from 'ctm-util-functions';
import { getBrand } from 'ctm-app/client/store/selectors';
import { PREFILL_INELIGIBLE_BRANDS } from 'ctm-components/components/prefill/utils';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';

const Container = styled.div`
    font-weight: bold;
    margin-top: 20vh;
    width: 100%;
    font-family: Arial, Helvetica, sans-serif;
    position: absolute;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    text-align: center;
`;

const Text = styled.h2`
    margin-bottom: 60px;
    font-size: 28px;
    font-weight: 600;
    line-height: 136%;
`;

const Spinner = () => {
    const { spinnerColour, spinnerType } = useBrandContentFactory();
    const isDefault = spinnerType() !== 'CircularProgress';

    // Sync animation phase with app.js spinner so it appears to continue seamlessly
    const dottedDelay = `-${Math.round((typeof performance !== 'undefined' ? performance.now() : 0) % 1700)}ms`;
    const ringDelay = `-${Math.round((typeof performance !== 'undefined' ? performance.now() : 0) % 1000)}ms`;

    if (isDefault) {
        const dottedKeyframes = `
            @keyframes prefillCheckLoad6 {
                0%     { box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em; }
                5%,95% { box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em; }
                10%,59%{ box-shadow: 0 -0.83em 0 -0.4em, -0.087em -0.825em 0 -0.42em, -0.173em -0.812em 0 -0.44em, -0.256em -0.789em 0 -0.46em, -0.297em -0.775em 0 -0.477em; }
                20%    { box-shadow: 0 -0.83em 0 -0.4em, -0.338em -0.758em 0 -0.42em, -0.555em -0.617em 0 -0.44em, -0.671em -0.488em 0 -0.46em, -0.749em -0.34em 0 -0.477em; }
                38%    { box-shadow: 0 -0.83em 0 -0.4em, -0.377em -0.74em 0 -0.42em, -0.645em -0.522em 0 -0.44em, -0.775em -0.297em 0 -0.46em, -0.82em -0.09em 0 -0.477em; }
                100%   { box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em; }
            }
            @keyframes prefillCheckRound {
                0%   { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        return (
            <div
                style={{
                    color: spinnerColour(),
                    fontSize: '80px',
                    width: '1.1em',
                    height: '1.1em',
                    borderRadius: '50%',
                    position: 'relative',
                    margin: '0 auto',
                    transform: 'translateZ(0)',
                    animation: `prefillCheckLoad6 1.7s ${dottedDelay} infinite ease, prefillCheckRound 1.7s ${dottedDelay} infinite ease`,
                    boxSizing: 'content-box'
                }}
            >
                <style>{dottedKeyframes}</style>
            </div>
        );
    }

    return (
        <div
            style={{
                margin: '0 auto',
                width: '85px',
                height: '85px',
                borderRadius: '50%',
                border: `8.5px solid ${spinnerColour()}`,
                borderBottomColor: 'transparent',
                animation: `prefillCheckSpin 1s ${ringDelay} infinite linear`,
                boxSizing: 'content-box'
            }}
        >
            <style>{'@keyframes prefillCheckSpin { to { transform: rotate(360deg); } }'}</style>
        </div>
    );
};

const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

const AUTH0_TOKEN_EXCHANGE_POLL_INTERVAL = 100;

/**
 * Asynchronously fetches data using axios and performs necessary updates based on the retrieved data.
 *
 * @return {Promise<void>} A Promise that resolves once the data fetching and updates are complete.
 */

const _PrefillCheck = ({ vertical, journeyUrl, accountUrl, updateTokens, trackPrefillCheckSuccess, trackPrefillCheckFailed, brandCode, auth0Token }) => {
    const anonId = sessionStorage.getItem('user_anonymous_id');
    const userId = sessionStorage.getItem('user_id');
    const sessionId = sessionStorage.getItem('sessionID');
    const expId = sessionStorage.getItem('experience-id');
    const accessToken = localStorage.getItem('access_token');
    const queryString = window?.location?.search;

    // eslint-disable-next-line consistent-return
    const handleTokens = async () => {
        let isExpired = false;
        let isExistingTokenUserAssigned = false;

        if (accessToken !== null && accessToken !== undefined) {
            const decodedJwt = parseJwt(accessToken);
            if (decodedJwt !== null) {
                isExpired = decodedJwt.exp * 1000 < Date.now();
                isExistingTokenUserAssigned = decodedJwt.sub !== null && decodedJwt.sub !== undefined;
            }
        }

        if (sessionStorage.getItem('auth0_transfer_started') === 'true') {
            // Wait for the Auth0 token exchange to complete before deciding if we need to fetch a new token.
            while (
                sessionStorage.getItem('auth0_transfer_started') === 'true'
                && sessionStorage.getItem('auth0_transfer_completed') !== 'true'
                && sessionStorage.getItem('auth0_token_exchange_failed') !== 'true'
            ) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => setTimeout(resolve, AUTH0_TOKEN_EXCHANGE_POLL_INTERVAL));
            }
        }

        // grab a new token if the current one:
        // - does not exist
        // - is expired
        // - if the auth0_transfer_completed flag is set to true (this means the Auth0 token exchange has completed,
        //      and a new token with this user assigned to it must be created)
        if (
            accessToken === null
            || accessToken === undefined
            || (accessToken && isExpired)
            || sessionStorage.getItem('auth0_transfer_completed') === 'true'
        ) {
            try {
                const auth0TransferCompleted = sessionStorage.getItem('auth0_transfer_completed') === 'true';
                const state = pageContext.getProps().store.getState();
                const auth0AccessToken = state.getIn(['vertical', 'quote', 'authUser', 'token']) || auth0Token;

                // if the auth0 transfer is completed and the user is already assigned to the token, do not request a new one
                const isAuthenticatedUserPageRefresh = auth0TransferCompleted && isExistingTokenUserAssigned && !auth0AccessToken;
                if (!isAuthenticatedUserPageRefresh) {
                    const auth0RequestConfig = auth0TransferCompleted && auth0AccessToken ? { headers: { Authorization: `Bearer ${auth0AccessToken}` } } : null;
                    const anonymousTokenUrl = `${accountUrl}/token/anonymous/${brandCode}${auth0RequestConfig && sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : ''}`;
                    const { data } = auth0RequestConfig ? await axios.get(anonymousTokenUrl, auth0RequestConfig) : await axios.get(anonymousTokenUrl);

                    updateTokens(data.access_token, data.refresh_token);
                    localStorage.setItem('access_token', data.access_token);
                    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
                    return data.access_token;
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error', error);
            }
        }
    };

    const fetchData = async () => {
        try {
            sessionStorage.setItem('has_prefill_candidate', 'false');
            const token = await handleTokens();
            // Normalise regardless of whether journeyUrl (serverConfig.journeyServiceBaseUrl)
            // was configured with or without a trailing slash, to avoid a double slash (which
            // some backends reject with a 400) or a missing slash entirely.
            const baseUrl = journeyUrl?.replace(/\/+$/, '');
            const { data: journeyData } = await axios.get(`${baseUrl}/journey/prefill/check`, {
                headers: { Authorization: `Bearer ${token || accessToken}` },
                params: {
                    brand_code: brandCode,
                    anonymous_id: anonId,
                    user_id: userId,
                    experiment_id: expId
                }
            });

            // prevent choo from prefilling regardless of the response that is received
            // this be because choo does not have the json schema setup for /prefill_verify
            // and will crash if it tries to prefill
            if (!PREFILL_INELIGIBLE_BRANDS.includes(brandCode) && journeyData.hasPrefillCandidate) {
                sessionStorage.setItem('has_prefill_candidate', 'true');
                pageContext.getProps().browserHistory.push({
                    pathname: `/${vertical}/journey/start`,
                    search: queryString
                });
                trackPrefillCheckSuccess();
            } else {
                pageContext.getProps().browserHistory.push({
                    pathname: `/${vertical}/journey/start`,
                    search: queryString
                });
                trackPrefillCheckFailed();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('error', error);
            pageContext.getProps().browserHistory.push({
                pathname: `/${vertical}/journey/start`,
                search: queryString
            });
            trackPrefillCheckFailed();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const pageState = pageContext.getProps().pageState;

    const pagesToExclude = ['/car-insurance/journey/results', '/home-contents-insurance/journey/results', '/car-insurance/journey/verify'];

    return (
        <Container>
            {!pagesToExclude.includes(pageState) && (
                <>
                    <Text>Just a moment...</Text>
                    <Spinner />
                </>
            )}
        </Container>
    );
};

const mapStateToProps = (state) => {
    const vertical = state.getIn(['page', 'vertical']);
    const journeyUrl = state.getIn(['serverConfig', 'journeyServiceBaseUrl']);
    const accountUrl = state.getIn(['serverConfig', 'accountServiceBaseUrl']);
    const env = state.getIn(['serverConfig', 'env']);
    const brandCode = getBrand(state);
    const auth0Token = state.getIn(['vertical', 'quote', 'authUser', 'token']);

    return { vertical, journeyUrl, env, accountUrl, brandCode, auth0Token };
};

const mapDispatchToProps = (dispatch) => ({
    updateTokens: (accessToken, refreshToken) => {
        dispatch({ type: 'SET_AUTH_TOKENS', access_token: accessToken, refresh_token: refreshToken });
    },
    trackPrefillCheckSuccess: () => {
        dispatch({ type: 'PREFILL_CHECK_SUCCESS' });
    },
    trackPrefillCheckFailed: () => {
        dispatch({ type: 'PREFILL_CHECK_FAILED' });
    }
});

_PrefillCheck.propTypes = {
    vertical: PropTypes.string,
    journeyUrl: PropTypes.string,
    accountUrl: PropTypes.string,
    updateTokens: PropTypes.func,
    trackPrefillCheckSuccess: PropTypes.func,
    trackPrefillCheckFailed: PropTypes.func,
    brandCode: PropTypes.string,
    auth0Token: PropTypes.string
};

const PrefillCheck = connect(mapStateToProps, mapDispatchToProps)(_PrefillCheck);

export { _PrefillCheck, PrefillCheck, parseJwt };
